import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Badge, Alert, Spinner } from 'react-bootstrap';
import { FaUserShield, FaUserTimes, FaUsers, FaEdit, FaCheck, FaTimes, FaUserPlus } from 'react-icons/fa';
import api from '../services/api';

const AdminUserManagementPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statistics, setStatistics] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Modal states
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newRole, setNewRole] = useState('');
    const [reason, setReason] = useState('');
    const [updating, setUpdating] = useState(false);

    // YENİ: Add User Modal states
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [addingUser, setAddingUser] = useState(false);
    const [newUserData, setNewUserData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        userType: 'individual',
        dealerName: '',
        address: ''
    });

    // Filter states
    const [filters, setFilters] = useState({
        search: '',
        userType: 'all'
    });

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    useEffect(() => {
        fetchUsers();
        fetchStatistics();
    }, [filters, pagination.page]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/users/management', {
                params: {
                    ...filters,
                    page: pagination.page,
                    limit: pagination.limit
                }
            });

            setUsers(response.data.users || []);
            if (response.data.pagination) {
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error('Kullanıcılar yüklenirken hata:', error);
            setError('Kullanıcılar yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            const response = await api.get('/users/management/statistics');
            setStatistics(response.data.statistics);
        } catch (error) {
            console.error('İstatistikler yüklenirken hata:', error);
        }
    };

    const handleRoleChange = async () => {
        try {
            setUpdating(true);
            setError('');

            await api.put(`/users/management/${selectedUser._id}/role`, {
                userType: newRole,
                reason
            });

            setSuccess(`${selectedUser.name} kullanıcısının yetkisi başarıyla güncellendi`);
            fetchUsers();
            fetchStatistics();
            setShowRoleModal(false);
            setSelectedUser(null);
            setNewRole('');
            setReason('');

            // Success mesajını 3 saniye sonra kapat
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Yetki değiştirme hatası:', error);
            setError(error.response?.data?.message || 'Yetki değiştirme hatası');
        } finally {
            setUpdating(false);
        }
    };

    const handleStatusToggle = async (userId, userName) => {
        try {
            setError('');
            await api.put(`/users/management/${userId}/status`);

            setSuccess(`${userName} kullanıcısının durumu başarıyla değiştirildi`);
            fetchUsers();
            fetchStatistics();

            // Success mesajını 3 saniye sonra kapat
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Durum değiştirme hatası:', error);
            setError(error.response?.data?.message || 'Durum değiştirme hatası');
        }
    };

    // YENİ: Add User fonksiyonu
    const handleAddUser = async () => {
        try {
            setAddingUser(true);
            setError('');

            // Client-side validation
            if (!newUserData.name || !newUserData.email || !newUserData.password || !newUserData.phone) {
                setError('Ad, email, şifre ve telefon zorunludur');
                return;
            }

            if (newUserData.password.length < 6) {
                setError('Şifre en az 6 karakter olmalıdır');
                return;
            }

            if (newUserData.userType === 'dealer' && !newUserData.dealerName) {
                setError('Dealer için "Dealer Adı" zorunludur');
                return;
            }

            const response = await api.post('/users/admin-create', newUserData);

            if (response.data.success) {
                setSuccess(`${newUserData.name} kullanıcısı başarıyla oluşturuldu`);
                fetchUsers();
                fetchStatistics();
                setShowAddUserModal(false);

                // Form'u sıfırla
                setNewUserData({
                    name: '',
                    email: '',
                    password: '',
                    phone: '',
                    userType: 'individual',
                    dealerName: '',
                    address: ''
                });

                // Success mesajını 3 saniye sonra kapat
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (error) {
            console.error('Kullanıcı oluşturma hatası:', error);
            setError(error.response?.data?.message || 'Kullanıcı oluşturma hatası');
        } finally {
            setAddingUser(false);
        }
    };

    const openRoleModal = (user) => {
        setSelectedUser(user);
        setNewRole(user.userType);
        setReason('');
        setShowRoleModal(true);
    };

    const openAddUserModal = () => {
        setNewUserData({
            name: '',
            email: '',
            password: '',
            phone: '',
            userType: 'individual',
            dealerName: '',
            address: ''
        });
        setShowAddUserModal(true);
    };

    const getRoleBadgeVariant = (userType) => {
        switch (userType) {
            case 'admin': return 'danger';
            case 'dealer': return 'warning';
            case 'representative': return 'info';
            case 'marketing': return 'success';  // ← BU SATIRI EKLE
            default: return 'secondary';
        }
    };

    const getRoleDisplayName = (userType) => {
        switch (userType) {
            case 'admin': return 'Admin';
            case 'dealer': return 'Dealer';
            case 'representative': return 'Temsilci';
            case 'individual': return 'Bireysel';
            case 'marketing': return 'Pazarlama';  // ← BU SATIRI EKLE
            default: return userType;
        }
    };

    return (
        <Container className="py-5">
            <Row className="mb-4">
                <Col className="d-flex justify-content-between align-items-center">
                    <div>
                        <h1>
                            <FaUsers className="me-2" />
                            Kullanıcı Yönetimi
                        </h1>
                        <p className="text-muted">Kullanıcı yetkilerini ve durumlarını yönetin</p>
                    </div>
                    {/* YENİ: Add User Butonu */}
                    <Button
                        variant="success"
                        onClick={openAddUserModal}
                        size="lg"
                    >
                        <FaUserPlus className="me-2" />
                        Yeni Kullanıcı Ekle
                    </Button>
                </Col>
            </Row>

            {/* Alerts */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* İstatistikler */}
            {statistics && (
                <Row className="mb-4">
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <h3 className="text-primary">{statistics.total}</h3>
                                <p className="text-muted mb-0">Toplam Kullanıcı</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <h3 className="text-success">{statistics.active}</h3>
                                <p className="text-muted mb-0">Aktif Kullanıcı</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <h3 className="text-danger">{statistics.inactive}</h3>
                                <p className="text-muted mb-0">Pasif Kullanıcı</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <h3 className="text-warning">
                                    {statistics.byType?.find(t => t._id === 'admin')?.count || 0}
                                </h3>
                                <p className="text-muted mb-0">Admin</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Filtreler */}
            <Card className="mb-4">
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Control
                                type="text"
                                placeholder="Kullanıcı ara (isim, email, telefon)"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                value={filters.userType}
                                onChange={(e) => setFilters({ ...filters, userType: e.target.value })}
                            >
                                <option value="all">Tüm Tipler</option>
                                <option value="admin">Admin</option>
                                <option value="dealer">Dealer</option>
                                <option value="representative">Temsilci</option>
                                <option value="marketing">Pazarlama</option>  // ← EKLE
                                <option value="individual">Bireysel</option>
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Button variant="primary" onClick={fetchUsers} disabled={loading}>
                                {loading ? <Spinner size="sm" animation="border" /> : 'Yenile'}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Kullanıcı Listesi */}
            <Card>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                            <p className="mt-2">Kullanıcılar yükleniyor...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="mb-0">
                                <thead className="table-dark">
                                    <tr>
                                        <th>Kullanıcı</th>
                                        <th>Email</th>
                                        <th>Telefon</th>
                                        <th>Yetki</th>
                                        <th>Durum</th>
                                        <th>Kayıt Tarihi</th>
                                        <th>İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.length > 0 ? (
                                        users.map(user => (
                                            <tr key={user._id}>
                                                <td>
                                                    <div>
                                                        <strong>{user.name}</strong>
                                                        {user.dealerName && (
                                                            <><br /><small className="text-muted">{user.dealerName}</small></>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{user.email}</td>
                                                <td>{user.phone}</td>
                                                <td>
                                                    <Badge bg={getRoleBadgeVariant(user.userType)}>
                                                        {getRoleDisplayName(user.userType)}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <Badge bg={user.isActive ? 'success' : 'secondary'}>
                                                        {user.isActive ? 'Aktif' : 'Pasif'}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {new Date(user.createdAt).toLocaleDateString('tr-TR')}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <Button
                                                            variant="warning"
                                                            size="sm"
                                                            onClick={() => openRoleModal(user)}
                                                            title="Yetki Değiştir"
                                                        >
                                                            <FaEdit />
                                                        </Button>
                                                        <Button
                                                            variant={user.isActive ? 'danger' : 'success'}
                                                            size="sm"
                                                            onClick={() => handleStatusToggle(user._id, user.name)}
                                                            title={user.isActive ? 'Pasif Yap' : 'Aktif Yap'}
                                                        >
                                                            {user.isActive ? <FaTimes /> : <FaCheck />}
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">
                                                Kullanıcı bulunamadı
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Button
                        variant="outline-primary"
                        disabled={pagination.page <= 1}
                        onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                        className="me-2"
                    >
                        Önceki
                    </Button>
                    <span className="align-self-center mx-3">
                        Sayfa {pagination.page} / {pagination.pages}
                    </span>
                    <Button
                        variant="outline-primary"
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    >
                        Sonraki
                    </Button>
                </div>
            )}

            {/* YENİ: Add User Modal */}
            <Modal show={showAddUserModal} onHide={() => setShowAddUserModal(false)} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaUserPlus className="me-2" />
                        Yeni Kullanıcı Ekle
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Ad Soyad *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newUserData.name}
                                    onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                                    placeholder="Kullanıcının adı soyadı"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Email *</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={newUserData.email}
                                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                                    placeholder="email@example.com"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Şifre *</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={newUserData.password}
                                    onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                                    placeholder="En az 6 karakter"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Telefon *</Form.Label>
                                <Form.Control
                                    type="tel"
                                    value={newUserData.phone}
                                    onChange={(e) => setNewUserData({ ...newUserData, phone: e.target.value })}
                                    placeholder="05555555555"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Kullanıcı Tipi *</Form.Label>
                                <Form.Select
                                    value={newUserData.userType}
                                    onChange={(e) => setNewUserData({ ...newUserData, userType: e.target.value })}
                                >
                                    <option value="individual">Bireysel Kullanıcı</option>
                                    <option value="dealer">Dealer</option>
                                    <option value="representative">Temsilci</option>
                                    <option value="marketing">Pazarlama</option>  
                                    <option value="admin">Admin</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            {newUserData.userType === 'dealer' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Dealer Adı *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={newUserData.dealerName}
                                        onChange={(e) => setNewUserData({ ...newUserData, dealerName: e.target.value })}
                                        placeholder="Dealer şirket adı"
                                    />
                                </Form.Group>
                            )}
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Adres</Form.Label>
                        <Form.Control
                            type="text"
                            value={newUserData.address}
                            onChange={(e) => setNewUserData({ ...newUserData, address: e.target.value })}
                            placeholder="Adres bilgisi (opsiyonel)"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowAddUserModal(false)}
                        disabled={addingUser}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleAddUser}
                        disabled={addingUser}
                    >
                        {addingUser ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Oluşturuluyor...
                            </>
                        ) : (
                            <>
                                <FaUserPlus className="me-2" />
                                Kullanıcı Oluştur
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Yetki Değiştirme Modal */}
            <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaUserShield className="me-2" />
                        Kullanıcı Yetkisi Değiştir
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedUser && (
                        <>
                            <div className="mb-3">
                                <strong>Kullanıcı:</strong> {selectedUser.name}<br />
                                <strong>Email:</strong> {selectedUser.email}<br />
                                <strong>Mevcut Yetki:</strong>{' '}
                                <Badge bg={getRoleBadgeVariant(selectedUser.userType)}>
                                    {getRoleDisplayName(selectedUser.userType)}
                                </Badge>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>Yeni Yetki Seviyesi</Form.Label>
                                <Form.Select
                                    value={newRole}
                                    onChange={(e) => setNewRole(e.target.value)}
                                >
                                    <option value="individual">Bireysel Kullanıcı</option>
                                    <option value="dealer">Dealer</option>
                                    <option value="representative">Temsilci</option>
                                    <option value="admin">Admin</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Değişiklik Sebebi</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Yetki değişikliği sebebini yazınız..."
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowRoleModal(false)}
                        disabled={updating}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleRoleChange}
                        disabled={updating || newRole === selectedUser?.userType}
                    >
                        {updating ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Güncelleniyor...
                            </>
                        ) : (
                            'Güncelle'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminUserManagementPage;